import React, { useMemo, useState, useRef, Component } from 'react';
import { motion } from 'framer-motion';
import { Sun, Moon, Star, Heart, Briefcase, Activity, Sparkles, Calendar as CalendarIcon, Hash, Palette, Smile, ChevronLeft, ChevronRight } from 'lucide-react';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Card } from '../components/ui/Card';
import { AdSlot } from '../components/ads/AdSlot';
interface DailyHoroscopePageProps {
  onNavigate: (page: string) => void;
}
const ZODIAC_SIGNS = [{
  id: 'aries',
  name: 'Овен',
  symbol: '♈',
  date: '21 мар - 19 апр',
  color: 'rose'
}, {
  id: 'taurus',
  name: 'Телец',
  symbol: '♉',
  date: '20 апр - 20 мая',
  color: 'emerald'
}, {
  id: 'gemini',
  name: 'Близнецы',
  symbol: '♊',
  date: '21 мая - 20 июн',
  color: 'amber'
}, {
  id: 'cancer',
  name: 'Рак',
  symbol: '♋',
  date: '21 июн - 22 июл',
  color: 'slate'
}, {
  id: 'leo',
  name: 'Лев',
  symbol: '♌',
  date: '23 июл - 22 авг',
  color: 'orange'
}, {
  id: 'virgo',
  name: 'Дева',
  symbol: '♍',
  date: '23 авг - 22 сен',
  color: 'teal'
}, {
  id: 'libra',
  name: 'Весы',
  symbol: '♎',
  date: '23 сен - 22 окт',
  color: 'pink'
}, {
  id: 'scorpio',
  name: 'Скорпион',
  symbol: '♏',
  date: '23 окт - 21 ноя',
  color: 'purple'
}, {
  id: 'sagittarius',
  name: 'Стрелец',
  symbol: '♐',
  date: '22 ноя - 21 дек',
  color: 'indigo'
}, {
  id: 'capricorn',
  name: 'Козерог',
  symbol: '♑',
  date: '22 дек - 19 янв',
  color: 'slate'
}, {
  id: 'aquarius',
  name: 'Водолей',
  symbol: '♒',
  date: '20 янв - 18 фев',
  color: 'cyan'
}, {
  id: 'pisces',
  name: 'Рыбы',
  symbol: '♓',
  date: '19 фев - 20 мар',
  color: 'sky'
}];
// STATIC COLOR MAP FOR TAILWIND JIT
const SIGN_STYLES: Record<string, {
  bg50: string;
  bg100: string;
  bg500: string;
  text500: string;
  text700: string;
  border500: string;
}> = {
  rose: {
    bg50: 'bg-rose-50',
    bg100: 'bg-rose-100',
    bg500: 'bg-rose-500',
    text500: 'text-rose-500',
    text700: 'text-rose-700',
    border500: 'border-rose-500'
  },
  emerald: {
    bg50: 'bg-emerald-50',
    bg100: 'bg-emerald-100',
    bg500: 'bg-emerald-500',
    text500: 'text-emerald-500',
    text700: 'text-emerald-700',
    border500: 'border-emerald-500'
  },
  amber: {
    bg50: 'bg-amber-50',
    bg100: 'bg-amber-100',
    bg500: 'bg-amber-500',
    text500: 'text-amber-500',
    text700: 'text-amber-700',
    border500: 'border-amber-500'
  },
  slate: {
    bg50: 'bg-slate-50',
    bg100: 'bg-slate-100',
    bg500: 'bg-slate-500',
    text500: 'text-slate-500',
    text700: 'text-slate-700',
    border500: 'border-slate-500'
  },
  orange: {
    bg50: 'bg-orange-50',
    bg100: 'bg-orange-100',
    bg500: 'bg-orange-500',
    text500: 'text-orange-500',
    text700: 'text-orange-700',
    border500: 'border-orange-500'
  },
  teal: {
    bg50: 'bg-teal-50',
    bg100: 'bg-teal-100',
    bg500: 'bg-teal-500',
    text500: 'text-teal-500',
    text700: 'text-teal-700',
    border500: 'border-teal-500'
  },
  pink: {
    bg50: 'bg-pink-50',
    bg100: 'bg-pink-100',
    bg500: 'bg-pink-500',
    text500: 'text-pink-500',
    text700: 'text-pink-700',
    border500: 'border-pink-500'
  },
  purple: {
    bg50: 'bg-purple-50',
    bg100: 'bg-purple-100',
    bg500: 'bg-purple-500',
    text500: 'text-purple-500',
    text700: 'text-purple-700',
    border500: 'border-purple-500'
  },
  indigo: {
    bg50: 'bg-indigo-50',
    bg100: 'bg-indigo-100',
    bg500: 'bg-indigo-500',
    text500: 'text-indigo-500',
    text700: 'text-indigo-700',
    border500: 'border-indigo-500'
  },
  cyan: {
    bg50: 'bg-cyan-50',
    bg100: 'bg-cyan-100',
    bg500: 'bg-cyan-500',
    text500: 'text-cyan-500',
    text700: 'text-cyan-700',
    border500: 'border-cyan-500'
  },
  sky: {
    bg50: 'bg-sky-50',
    bg100: 'bg-sky-100',
    bg500: 'bg-sky-500',
    text500: 'text-sky-500',
    text700: 'text-sky-700',
    border500: 'border-sky-500'
  }
};
const PERIODS = [{
  id: 'today',
  label: 'Сегодня'
}, {
  id: 'tomorrow',
  label: 'Завтра'
}, {
  id: 'week',
  label: 'Неделя'
}, {
  id: 'month',
  label: 'Месяц'
}];
// Mock data generator for varying content
const generateMockData = (signId: string, periodId: string) => {
  // Simple hash to make data deterministic but varied
  const hash = signId.charCodeAt(0) + signId.charCodeAt(signId.length - 1) + periodId.length;
  const forecasts = ['Сегодня отличный день для новых начинаний и смелых решений. Звезды благоприятствуют тем, кто готов взять инициативу в свои руки. Ваша интуиция будет особенно острой — прислушивайтесь к внутреннему голосу при принятии важных решений. Возможны неожиданные, но приятные новости издалека.', 'Энергия этого периода направлена на внутреннюю трансформацию. Отличное время для завершения старых дел и освобождения пространства для нового. В отношениях возможны глубокие разговоры, которые выведут вас на новый уровень понимания. Финансовые вопросы потребуют внимательности.', 'Ваша социальная активность на пике! Ожидайте интересных встреч и полезных знакомств. Звезды советуют быть открытыми к новым предложениям — одно из них может оказаться судьбоносным. В сфере здоровья обратите внимание на режим сна и питания.', 'Время сфокусироваться на карьерных целях и материальном благополучии. Ваш труд будет замечен и оценен по достоинству. В личной жизни возможны небольшие недопонимания, которые легко разрешатся, если вы проявите терпение и готовность к компромиссу.'];
  const numbers = ['7, 14, 21', '3, 9, 12', '5, 18, 22', '1, 8, 19', '4, 11, 27'];
  const colors = [{
    name: 'Изумрудный',
    bg: 'bg-emerald-500'
  }, {
    name: 'Индиго',
    bg: 'bg-indigo-500'
  }, {
    name: 'Коралловый',
    bg: 'bg-rose-400'
  }, {
    name: 'Золотой',
    bg: 'bg-amber-400'
  }, {
    name: 'Лазурный',
    bg: 'bg-sky-500'
  }];
  const moods = ['Вдохновленное', 'Спокойное', 'Энергичное', 'Задумчивое', 'Решительное'];
  return {
    forecast: forecasts[hash % forecasts.length],
    loveScore: 60 + hash % 40,
    careerScore: 50 + hash * 2 % 50,
    healthScore: 70 + hash * 3 % 30,
    number: numbers[hash % numbers.length],
    color: colors[hash % colors.length],
    mood: moods[hash % moods.length]
  };
};
// Rating Bar Component
const RatingBar = ({
  score,
  colorClass
}: {
  score: number;
  colorClass: string;
}) => <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mt-3" data-id="element-18642">
    <motion.div initial={{
    width: 0
  }} animate={{
    width: `${score}%`
  }} transition={{
    duration: 1,
    ease: 'easeOut'
  }} className={`h-full ${colorClass} rounded-full`} data-id="element-18643" />
  </div>;
export function DailyHoroscopePage({
  onNavigate
}: DailyHoroscopePageProps) {
  const [activeSign, setActiveSign] = useState(ZODIAC_SIGNS[0].id);
  const [activePeriod, setActivePeriod] = useState('today');
  const zodiacScrollRef = useRef<HTMLDivElement>(null);
  const selectedSign = ZODIAC_SIGNS.find(s => s.id === activeSign) || ZODIAC_SIGNS[0];
  const styles = SIGN_STYLES[selectedSign.color] || SIGN_STYLES.slate;
  const scrollZodiac = (direction: 'left' | 'right') => {
    if (zodiacScrollRef.current) {
      const scrollAmount = 200;
      zodiacScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  const data = useMemo(() => generateMockData(activeSign, activePeriod), [activeSign, activePeriod]);
  return <div className="min-h-screen bg-slate-50 pb-16 sm:pb-24" data-id="element-18644">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 pt-6 pb-6 sm:pb-8" data-id="element-18645">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-18646">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Гороскоп на сегодня'
        }]} className="mb-6" data-id="element-18647" />

          <div className="text-center max-w-2xl mx-auto mb-8" data-id="element-18648">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-indigo-100 text-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-sm" data-id="element-18649">
              <Sun className="w-6 h-6 sm:w-8 sm:h-8" data-id="element-18650" />
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-18651">
              Ежедневный гороскоп
            </h1>
            <p className="text-base sm:text-lg text-slate-600 px-4 sm:px-0" data-id="element-18652">
              Узнайте, что приготовили для вас звезды. Персональный прогноз для
              каждого знака зодиака.
            </p>
          </div>

          {/* Zodiac Selector — single row with horizontal scroll + arrows */}
          <div className="relative -mx-4 sm:mx-0" data-id="element-18653">
            {/* Left arrow */}
            <button onClick={() => scrollZodiac('left')} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white/90 shadow-md border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-white hover:text-slate-900 transition-all lg:hidden" aria-label="Прокрутить влево" data-id="element-18654">
              <ChevronLeft className="w-4 h-4" data-id="element-18655" />
            </button>

            <div ref={zodiacScrollRef} className="flex overflow-x-auto hide-scrollbar gap-1 sm:gap-1.5 pb-2 px-8 sm:px-0 lg:px-0 snap-x scroll-smooth" data-id="element-18656">
              {ZODIAC_SIGNS.map(sign => {
              const signStyle = SIGN_STYLES[sign.color] || SIGN_STYLES.slate;
              const isActive = activeSign === sign.id;
              return <button key={sign.id} onClick={() => setActiveSign(sign.id)} className={`flex flex-col items-center min-w-[64px] sm:min-w-[72px] lg:min-w-0 lg:flex-1 py-2 px-2 sm:px-3 rounded-xl transition-all snap-center shrink-0 ${isActive ? `${signStyle.bg50} border ${signStyle.border500} shadow-sm` : 'bg-white border border-transparent hover:bg-slate-50 hover:border-slate-200'}`} data-id="element-18657">
                    <span className="text-xl sm:text-2xl mb-0.5 sm:mb-1" data-id="element-18658">
                      {sign.symbol}
                    </span>
                    <span className={`text-[11px] sm:text-xs font-bold leading-tight ${isActive ? signStyle.text700 : 'text-slate-700'}`} data-id="element-18659">
                      {sign.name}
                    </span>
                    <span className="text-[8px] sm:text-[9px] text-slate-400 mt-0.5 whitespace-nowrap" data-id="element-18660">
                      {sign.date}
                    </span>
                  </button>;
            })}
            </div>

            {/* Right arrow */}
            <button onClick={() => scrollZodiac('right')} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white/90 shadow-md border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-white hover:text-slate-900 transition-all lg:hidden" aria-label="Прокрутить вправо" data-id="element-18661">
              <ChevronRight className="w-4 h-4" data-id="element-18662" />
            </button>

            {/* Fade edges */}
            <div className="absolute top-0 left-8 w-4 h-full bg-gradient-to-r from-white to-transparent pointer-events-none lg:hidden" data-id="element-18663" />
            <div className="absolute top-0 right-8 w-4 h-full bg-gradient-to-l from-white to-transparent pointer-events-none lg:hidden" data-id="element-18664" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-id="element-18665">
        <motion.div key={`${activeSign}-${activePeriod}`} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-18666">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 sm:mb-8 gap-4" data-id="element-18667">
            <div className="flex items-center gap-4" data-id="element-18668">
              <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full ${styles.bg100} flex items-center justify-center text-2xl sm:text-3xl shadow-inner shrink-0`} data-id="element-18669">
                {selectedSign.symbol}
              </div>
              <div data-id="element-18670">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-18671">
                  {selectedSign.name}
                </h2>
                <p className="text-sm sm:text-base text-slate-500" data-id="element-18672">
                  {selectedSign.date}
                </p>
              </div>
            </div>

            <div className="bg-white p-1 rounded-xl shadow-sm border border-slate-200 flex overflow-x-auto hide-scrollbar" data-id="element-18673">
              {PERIODS.map(period => <button key={period.id} onClick={() => setActivePeriod(period.id)} className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${activePeriod === period.id ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'}`} data-id="element-18674">
                  {period.label}
                </button>)}
            </div>
          </div>

          {/* General Forecast */}
          <Card className="mb-6 sm:mb-8 overflow-hidden border-0 shadow-md" data-id="element-18675">
            <div className={`h-1.5 sm:h-2 ${styles.bg500} w-full`} data-id="element-18676" />
            <div className="p-5 sm:p-8" data-id="element-18677">
              <div className="flex items-center gap-2 mb-3 sm:mb-4" data-id="element-18678">
                <Sparkles className={`w-5 h-5 ${styles.text500}`} data-id="element-18679" />
                <h3 className="text-lg sm:text-xl font-bold text-slate-900" data-id="element-18680">
                  Общий прогноз
                </h3>
              </div>
              <p className="text-slate-700 leading-relaxed text-base sm:text-lg" data-id="element-18681">
                {data.forecast}
              </p>
            </div>
          </Card>

          {/* Categories Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8" data-id="element-18682">
            <Card padding="md" className="border-rose-100 bg-rose-50/30 hover:shadow-md transition-shadow flex flex-col" data-id="element-18683">
              <div className="flex items-center justify-between mb-3" data-id="element-18684">
                <div className="flex items-center gap-2" data-id="element-18685">
                  <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center" data-id="element-18686">
                    <Heart className="w-4 h-4 text-rose-600" data-id="element-18687" />
                  </div>
                  <h4 className="font-bold text-slate-900" data-id="element-18688">Любовь</h4>
                </div>
                <span className="text-sm font-bold text-rose-600" data-id="element-18689">
                  {data.loveScore}%
                </span>
              </div>
              <p className="text-sm text-slate-600 flex-grow" data-id="element-18690">
                Романтические энергии на подъеме. Отличное время для откровенных
                разговоров с партнером или новых знакомств.
              </p>
              <RatingBar score={data.loveScore} colorClass="bg-rose-500" data-id="element-18691" />
            </Card>

            <Card padding="md" className="border-indigo-100 bg-indigo-50/30 hover:shadow-md transition-shadow flex flex-col" data-id="element-18692">
              <div className="flex items-center justify-between mb-3" data-id="element-18693">
                <div className="flex items-center gap-2" data-id="element-18694">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center" data-id="element-18695">
                    <Briefcase className="w-4 h-4 text-indigo-600" data-id="element-18696" />
                  </div>
                  <h4 className="font-bold text-slate-900" data-id="element-18697">Карьера</h4>
                </div>
                <span className="text-sm font-bold text-indigo-600" data-id="element-18698">
                  {data.careerScore}%
                </span>
              </div>
              <p className="text-sm text-slate-600 flex-grow" data-id="element-18699">
                Проявите лидерские качества. Ваши идеи будут услышаны
                руководством. Возможны небольшие финансовые поступления.
              </p>
              <RatingBar score={data.careerScore} colorClass="bg-indigo-500" data-id="element-18700" />
            </Card>

            <Card padding="md" className="border-emerald-100 bg-emerald-50/30 hover:shadow-md transition-shadow flex flex-col sm:col-span-2 lg:col-span-1" data-id="element-18701">
              <div className="flex items-center justify-between mb-3" data-id="element-18702">
                <div className="flex items-center gap-2" data-id="element-18703">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center" data-id="element-18704">
                    <Activity className="w-4 h-4 text-emerald-600" data-id="element-18705" />
                  </div>
                  <h4 className="font-bold text-slate-900" data-id="element-18706">Здоровье</h4>
                </div>
                <span className="text-sm font-bold text-emerald-600" data-id="element-18707">
                  {data.healthScore}%
                </span>
              </div>
              <p className="text-sm text-slate-600 flex-grow" data-id="element-18708">
                Энергетический потенциал высок, но не забывайте об отдыхе.
                Вечерняя прогулка поможет снять напряжение дня.
              </p>
              <RatingBar score={data.healthScore} colorClass="bg-emerald-500" data-id="element-18709" />
            </Card>
          </div>

          {/* Daily Metrics */}
          <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 shadow-sm" data-id="element-18710">
            <div className="flex items-center gap-3 p-3 sm:p-0 rounded-xl sm:rounded-none bg-slate-50 sm:bg-transparent border border-slate-100 sm:border-none" data-id="element-18711">
              <div className="w-10 h-10 rounded-full bg-white sm:bg-slate-100 flex items-center justify-center shadow-sm sm:shadow-none shrink-0" data-id="element-18712">
                <Hash className="w-5 h-5 text-slate-500" data-id="element-18713" />
              </div>
              <div data-id="element-18714">
                <div className="text-[10px] sm:text-xs text-slate-500 font-medium uppercase tracking-wider" data-id="element-18715">
                  Число дня
                </div>
                <div className="text-base sm:text-lg font-bold text-slate-900" data-id="element-18716">
                  {data.number}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 sm:p-0 rounded-xl sm:rounded-none bg-slate-50 sm:bg-transparent border border-slate-100 sm:border-none" data-id="element-18717">
              <div className="w-10 h-10 rounded-full bg-white sm:bg-slate-100 flex items-center justify-center shadow-sm sm:shadow-none shrink-0" data-id="element-18718">
                <Palette className="w-5 h-5 text-slate-500" data-id="element-18719" />
              </div>
              <div data-id="element-18720">
                <div className="text-[10px] sm:text-xs text-slate-500 font-medium uppercase tracking-wider" data-id="element-18721">
                  Цвет удачи
                </div>
                <div className="text-base sm:text-lg font-bold text-slate-900 flex items-center gap-2" data-id="element-18722">
                  <span className={`w-3 h-3 rounded-full ${data.color.bg}`} data-id="element-18723"></span>
                  {data.color.name}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 sm:p-0 rounded-xl sm:rounded-none bg-slate-50 sm:bg-transparent border border-slate-100 sm:border-none" data-id="element-18724">
              <div className="w-10 h-10 rounded-full bg-white sm:bg-slate-100 flex items-center justify-center shadow-sm sm:shadow-none shrink-0" data-id="element-18725">
                <Smile className="w-5 h-5 text-slate-500" data-id="element-18726" />
              </div>
              <div data-id="element-18727">
                <div className="text-[10px] sm:text-xs text-slate-500 font-medium uppercase tracking-wider" data-id="element-18728">
                  Настроение
                </div>
                <div className="text-base sm:text-lg font-bold text-slate-900" data-id="element-18729">
                  {data.mood}
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar Ad */}
          <div className="mt-8" data-id="element-18730">
            <AdSlot slotId="SLOT-07" onNavigate={onNavigate} data-id="element-18731" />
          </div>

          {/* Cross-links */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-18732">
            <button onClick={() => onNavigate('moon-calendar')} className="flex items-center gap-4 p-4 sm:p-5 bg-white rounded-2xl border border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all text-left group" data-id="element-18733">
              <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center text-2xl flex-shrink-0 group-hover:bg-indigo-100 transition-colors" data-id="element-18734">
                🌙
              </div>
              <div data-id="element-18735">
                <div className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors" data-id="element-18736">
                  Лунный календарь
                </div>
                <div className="text-xs sm:text-sm text-slate-500" data-id="element-18737">
                  Фазы луны и рекомендации на сегодня
                </div>
              </div>
            </button>
            <button onClick={() => onNavigate('calendar')} className="flex items-center gap-4 p-4 sm:p-5 bg-white rounded-2xl border border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all text-left group" data-id="element-18738">
              <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center text-2xl flex-shrink-0 group-hover:bg-amber-100 transition-colors" data-id="element-18739">
                📅
              </div>
              <div data-id="element-18740">
                <div className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors" data-id="element-18741">
                  Астро-календарь
                </div>
                <div className="text-xs sm:text-sm text-slate-500" data-id="element-18742">
                  Затмения, ретрограды и важные даты
                </div>
              </div>
            </button>
          </div>

          {/* Inline Ad Banner */}
          <div className="mt-8" data-id="element-18743">
            <AdSlot slotId="SLOT-06" onNavigate={onNavigate} data-id="element-18744" />
          </div>
        </motion.div>

        {/* CTA */}
        <div className="mt-12 sm:mt-16 bg-gradient-to-br from-slate-900 to-indigo-950 rounded-3xl p-6 sm:p-12 text-center relative overflow-hidden shadow-xl" data-id="element-18745">
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-18746" />
          <div className="relative z-10" data-id="element-18747">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4" data-id="element-18748">
              Хотите персональный прогноз?
            </h2>
            <p className="text-sm sm:text-base text-indigo-200 mb-6 sm:mb-8 max-w-xl mx-auto" data-id="element-18749">
              Общий гороскоп — это только начало. Закажите индивидуальный
              прогноз транзитов, построенный по вашей натальной карте.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center" data-id="element-18750">
              <button onClick={() => onNavigate('service-forecast')} className="px-6 sm:px-8 py-3 bg-white text-slate-900 rounded-xl font-semibold hover:bg-slate-100 transition-colors text-sm sm:text-base" data-id="element-18751">
                Заказать прогноз транзитов
              </button>
              <button onClick={() => onNavigate('free-chart')} className="px-6 sm:px-8 py-3 border border-white/20 text-white rounded-xl font-semibold hover:bg-white/10 transition-colors text-sm sm:text-base" data-id="element-18752">
                Бесплатная карта
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}